import React, { useState } from 'react';
import { Heart, Clock, Users, Coffee, Smartphone, Baby, Calendar, MapPin, Send, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VisitPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', guest_count: 1, message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const resp = await fetch(`${BACKEND_URL}/api/rsvps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, guest_count: parseInt(formData.guest_count) || 1 }),
      });
      if (!resp.ok) throw new Error('Failed');
      setIsSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or email us at contact@elbethel.church');
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatToExpect = [
    { icon: Users, title: 'Warm Welcome', description: 'Our greeters will welcome you at the door and help you find your way around.' },
    { icon: Coffee, title: 'Meet & Greet (4:30 PM)', description: 'Arrive early for fellowship. Get to know our community before service begins.' },
    { icon: Clock, title: '90-Minute Service', description: 'Our Sunday worship runs from 5:00 PM to 6:30 PM, following the Acts 2:42 pattern.' },
    { icon: Baby, title: 'Family Friendly', description: 'Children are welcome in service. Sunday School runs concurrently at 5:30 PM.' },
    { icon: Smartphone, title: 'Come As You Are', description: 'We dress casually — jeans and t-shirts are perfectly fine!' },
    { icon: Coffee, title: 'Meal Together', description: 'Join us for a meal and fellowship after every Sunday service.' },
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero */}
      <section className="py-10 sm:py-14 lg:py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-white">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-[#f17b21] mx-auto mb-3" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-800 mb-4 font-display" data-testid="visit-title">
            You Are Welcome Here
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto">
            El Bethel Church is a close-knit community that gathers weekly for worship,
            biblical teaching, and fellowship in the Grand Prairie, TX area.
            We would love to have you join us!
          </p>
        </div>
      </section>

      {/* RSVP + Service Info */}
      <section className="py-8 sm:py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">

            {/* RSVP Form */}
            <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-5 sm:p-7 order-1" data-testid="rsvp-section">
              {!isSubmitted ? (
                <>
                  <div className="flex items-center gap-2.5 mb-1">
                    <Send className="w-5 h-5 text-[#f17b21]" />
                    <h2 className="text-lg sm:text-xl font-bold text-stone-800 font-display">RSVP for This Sunday</h2>
                  </div>
                  <p className="text-xs sm:text-sm text-stone-500 mb-5">
                    Let us know you are coming and we will send you the location details.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="rsvp-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-stone-600 mb-1">Name *</label>
                        <input type="text" name="name" required value={formData.name} onChange={handleChange}
                          placeholder="Your name" data-testid="rsvp-name"
                          className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-stone-600 mb-1">Email *</label>
                        <input type="email" name="email" required value={formData.email} onChange={handleChange}
                          placeholder="your@email.com" data-testid="rsvp-email"
                          className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-stone-600 mb-1">Phone <span className="text-stone-400 font-normal">(optional)</span></label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                          placeholder="(555) 123-4567" data-testid="rsvp-phone"
                          className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-stone-600 mb-1">How many guests?</label>
                        <select name="guest_count" value={formData.guest_count} onChange={handleChange} data-testid="rsvp-guests"
                          className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent text-sm bg-white">
                          {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1">Anything we should know? <span className="text-stone-400 font-normal">(optional)</span></label>
                      <textarea name="message" rows="2" value={formData.message} onChange={handleChange}
                        placeholder="e.g., bringing kids, dietary needs, questions..." data-testid="rsvp-message"
                        className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent text-sm resize-none" />
                    </div>
                    {error && <p className="text-red-600 text-xs" data-testid="rsvp-error">{error}</p>}
                    <button type="submit" disabled={isSubmitting} data-testid="rsvp-submit"
                      className="w-full bg-[#f17b21] hover:bg-orange-600 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm">
                      {isSubmitting ? 'Sending...' : <><Send className="w-4 h-4" /> Send My RSVP</>}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-6" data-testid="rsvp-success">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-800 mb-2 font-display">We Got Your RSVP!</h3>
                  <p className="text-sm text-stone-600 mb-2">
                    We will reach out shortly with the location details and everything you need for your visit.
                  </p>
                  <p className="text-xs text-stone-500 italic">We are looking forward to welcoming you this Sunday.</p>
                </div>
              )}
            </div>

            {/* Service Info + Location + QR */}
            <div className="space-y-5 order-2">
              <div className="bg-gradient-to-br from-[#f17b21] to-orange-600 rounded-2xl shadow-lg p-5 sm:p-7 text-white">
                <div className="flex items-center gap-2.5 mb-4">
                  <Clock className="w-5 h-5" />
                  <h3 className="font-bold text-lg">Sunday Service</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="opacity-90">Meet & Greet</span><span className="font-semibold">4:30 PM</span></div>
                  <div className="flex justify-between"><span className="opacity-90">Worship Service</span><span className="font-semibold">5:00 – 6:30 PM</span></div>
                  <div className="flex justify-between"><span className="opacity-90">Sunday School</span><span className="font-semibold">5:30 PM</span></div>
                  <div className="pt-2 border-t border-white/20 text-xs opacity-80">Fellowship meal served after every service</div>
                </div>
              </div>

              <div className="bg-stone-50 rounded-2xl border border-stone-200 p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#f17b21] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-stone-800 text-sm mb-1">Why we share location privately</h3>
                    <p className="text-xs text-stone-600 leading-relaxed">
                      We currently gather in a home setting for intimate worship — much like the early church.
                      For the privacy and safety of our host family, we share our meeting address directly with those who RSVP.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-5 text-center">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Share with a friend</p>
                <img src="/rsvp-qr.png" alt="Scan to RSVP" className="w-28 h-28 mx-auto mb-1" data-testid="rsvp-qr-code" />
                <p className="text-[10px] text-stone-400">Scan to open this page</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-10 sm:py-14 lg:py-16 bg-stone-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-800 mb-3 font-display">What to Expect</h2>
            <p className="text-sm sm:text-base text-stone-600 max-w-2xl mx-auto">
              Your first visit should be comfortable and welcoming. Here&apos;s what you can expect.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {whatToExpect.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-stone-100 p-5 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-orange-50 rounded-full flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[#f17b21]" />
                  </div>
                  <h3 className="font-bold text-stone-800 mb-1 text-sm">{item.title}</h3>
                  <p className="text-xs text-stone-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sunday Service Flow */}
      <section className="py-10 sm:py-14 lg:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <Calendar className="w-10 h-10 text-[#f17b21] mx-auto mb-3" />
          <h2 className="text-2xl sm:text-3xl font-bold text-stone-800 mb-4 font-display">Sunday Service Flow</h2>
          <p className="text-sm sm:text-base text-stone-600 mb-6">
            Our worship gathering follows the Acts 2:42 pattern with worship, teaching,
            communion, and prayer. Each element is designed to help us encounter God together.
          </p>
          <a href="/why-we-do"
            className="inline-block bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors text-sm">
            Learn Why We Do What We Do →
          </a>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-10 sm:py-12 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#f17b21] to-orange-600 rounded-2xl p-6 sm:p-10 text-center text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 font-display">We Can&apos;t Wait to Meet You!</h2>
            <p className="text-sm sm:text-base mb-6 opacity-90">Have questions before your visit? We&apos;re here to help.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/contact" className="bg-white text-[#f17b21] hover:bg-orange-50 px-6 py-3 rounded-lg font-semibold transition-colors text-sm">Contact Us</a>
              <a href="mailto:contact@elbethel.church" className="border-2 border-white text-white hover:bg-white hover:text-[#f17b21] px-6 py-3 rounded-lg font-semibold transition-colors text-sm">Email Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* Scripture + Closing */}
      <section className="py-6 sm:py-8 bg-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs sm:text-sm text-stone-500 italic mb-1">
            &ldquo;For where two or three gather in my name, there am I with them.&rdquo; — Matthew 18:20
          </p>
          <p className="text-sm text-stone-600 font-medium">We cannot wait to worship with you. See you soon!</p>
          <p className="text-xs text-stone-400 mt-3">
            Can&apos;t make it in person?{' '}
            <a href="https://elbethel.online" target="_blank" rel="noopener noreferrer" className="text-[#f17b21] hover:text-[#d86919] underline">
              Visit our online ministry
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default VisitPage;
